import * as React from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../Components/Card';
import { Badge } from '../../Components/Badge';
import { Button } from '../../Components/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../Components/Tabs';
import { EmailTemplateModal } from '../../Components/EmailTemplateModal';
import EmailTemplateService, { EmailTemplateDto } from '../../DataService/EmailTemplateService';
import { MembershipPlanService } from '../../DataService/MembershipPlanService';
import { PaymentService } from '../../DataService/PaymentService';
import { BookingService } from '../../DataService/BookingService';
import { usePaymentHub } from '../../DataService/PaymentHubService';
import { StripeCheckoutWrapper } from '../../Components/StripeCheckout';
import { CheckoutOrderRequest, CheckoutContactInfo } from '../../Types/CartTypes';
import { AvailabilityDto } from '../../Types/BookingServiceTypes';
import { MembershipPlanDto } from '../../Types/MembershipPlanTypes';
import ConsultingPricingService, { ConsultingPricingPublicDto, ConsultingPricingRuleDto, ConsultingPricingRuleType } from '../../DataService/ConsultingPricingService';
import { AdminLogsPanel } from './Components/AdminLogsPanel';
import { normalizeSwishPayerAlias } from '../../Utils/swishPhone';
import './AdminPortalTools.css';
import './AdminHome.css';

const feelgoodLabel = (type: string) => {
  if (type === 'feelgood-member') return 'Medlemmar';
  if (type === 'feelgood-courseowner') return 'Kursägare';
  return 'Båda';
};

type ConsultingRuleDraft = Omit<ConsultingPricingRuleDto, 'id' | 'createdAtUtc' | 'updatedAtUtc'>;

const emptyConsultingRule: ConsultingRuleDraft = {
  serviceKey: 'consulting',
  isActive: true,
  priority: 0,
  minSessions: 1,
  ruleType: ConsultingPricingRuleType.Percentage,
  percentOff: 0,
  fixedTotalPrice: null,
  freeSessions: null,
  displayText: '',
  snapshotLabel: '',
  startAtUtc: null,
  endAtUtc: null,
};

const toDateTimeLocalValue = (value?: string | null) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 16);
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const fromDateTimeLocalValue = (value: string) => (value ? new Date(value).toISOString() : null);

const normalizeConsultingRuleDraft = (draft: ConsultingRuleDraft): ConsultingRuleDraft => ({
  ...draft,
  serviceKey: 'consulting',
  displayText: draft.displayText.trim(),
  snapshotLabel: draft.snapshotLabel?.trim() ? draft.snapshotLabel.trim() : null,
  percentOff: draft.ruleType === ConsultingPricingRuleType.Percentage ? draft.percentOff : null,
  fixedTotalPrice: draft.ruleType === ConsultingPricingRuleType.FixedTotal ? draft.fixedTotalPrice : null,
  freeSessions: draft.ruleType === ConsultingPricingRuleType.BuyXGetY ? draft.freeSessions : null,
});

const validateConsultingRuleDraft = (draft: ConsultingRuleDraft): string | null => {
  if (!draft.displayText.trim()) return 'Skriv en synlig text för regeln.';
  if (!Number.isFinite(draft.priority)) return 'Prioritet måste vara ett nummer.';
  if (!Number.isFinite(draft.minSessions) || draft.minSessions < 1) return 'Min antal samtal måste vara minst 1.';

  if (draft.startAtUtc && draft.endAtUtc && new Date(draft.startAtUtc) > new Date(draft.endAtUtc)) {
    return 'Startdatum kan inte vara senare än slutdatum.';
  }

  if (draft.ruleType === ConsultingPricingRuleType.Percentage) {
    if (draft.percentOff == null || !Number.isFinite(draft.percentOff) || draft.percentOff <= 0 || draft.percentOff > 100) {
      return 'Procentrabatt måste vara mellan 1 och 100.';
    }
  }

  if (draft.ruleType === ConsultingPricingRuleType.FixedTotal) {
    if (draft.fixedTotalPrice == null || !Number.isFinite(draft.fixedTotalPrice) || draft.fixedTotalPrice < 0) {
      return 'Fast totalpris måste vara 0 eller högre.';
    }
  }

  if (draft.ruleType === ConsultingPricingRuleType.BuyXGetY) {
    if (draft.freeSessions == null || !Number.isFinite(draft.freeSessions) || draft.freeSessions < 1) {
      return 'Gratis samtal måste vara minst 1.';
    }
  }

  return null;
};

export const AdminPortalTools = () => {
  const [templates, setTemplates] = React.useState<EmailTemplateDto[]>([]);
  const [publicTemplates, setPublicTemplates] = React.useState<EmailTemplateDto[]>([]);
  const [editingTemplate, setEditingTemplate] = React.useState<EmailTemplateDto | null>(null);
  const [templateMode, setTemplateMode] = React.useState<'feelgood' | 'public'>('feelgood');
  const [showTemplateModal, setShowTemplateModal] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [membershipPlans, setMembershipPlans] = React.useState<MembershipPlanDto[]>([]);

  const [payerAlias, setPayerAlias] = React.useState('');
  const [amount, setAmount] = React.useState('1');
  const [message, setMessage] = React.useState('Live test payment from admin');
  const [paymentId, setPaymentId] = React.useState<string | null>(null);
  const [stripeType, setStripeType] = React.useState<'membership' | 'course' | 'program' | 'booking'>('membership');
  const [stripeTitle, setStripeTitle] = React.useState('Testprodukt');
  const [stripeAmount, setStripeAmount] = React.useState('1');
  const [stripeCourseId, setStripeCourseId] = React.useState('');
  const [availableBookingSlots, setAvailableBookingSlots] = React.useState<AvailabilityDto[]>([]);
  const [selectedBookingSlotId, setSelectedBookingSlotId] = React.useState<string>('');
  const [stripePayment, setStripePayment] = React.useState<{ paymentReference: string; amount: number } | null>(null);
  const [stripeSubscription, setStripeSubscription] = React.useState<{ subscriptionId: string; clientSecret: string; planKey: string } | null>(null);
  const [stripePreset, setStripePreset] = React.useState<'membership' | 'grow' | 'rot-registration' | 'rot-balance' | 'booking'>('membership');
  const [consultingPricing, setConsultingPricing] = React.useState<ConsultingPricingPublicDto | null>(null);
  const [consultingRuleDraft, setConsultingRuleDraft] = React.useState<ConsultingRuleDraft>(emptyConsultingRule);
  const [editingConsultingRuleId, setEditingConsultingRuleId] = React.useState<number | null>(null);
  const [consultingSaving, setConsultingSaving] = React.useState(false);
  const [stripeContact, setStripeContact] = React.useState<CheckoutContactInfo>({
    firstName: 'Admin',
    lastName: 'Test',
    email: 'admin@sandrasportal.local',
    phone: '0700000000',
    address: 'Admin street 1',
    city: 'Stockholm',
    postalCode: '11111',
  });

  const paymentStatus = usePaymentHub(paymentId ?? '');
  const stripeStatus = usePaymentHub(stripePayment?.paymentReference ?? '');
  const feelgoodCount = templates.length;
  const publicCopyCount = publicTemplates.length;
  const consultingRuleCount = consultingPricing?.rules?.length ?? 0;
  const swishState = paymentId ? (paymentStatus ?? 'Väntar på uppdatering...') : 'Ingen pågående testbetalning';
  const stripeState = stripePayment ? (stripeStatus ?? 'Väntar på uppdatering...') : 'Ingen pågående checkout';

  const stripePresets = {
    membership: {
      label: 'Medlemskap',
      type: 'membership' as const,
      title: 'GROW medlemskap',
      amount: '1',
      message: 'Test av medlemskap',
    },
    grow: {
      label: 'GROW premium',
      type: 'membership' as const,
      title: 'GROW Premium',
      amount: '1',
      message: 'Test av GROW Premium',
    },
    'rot-registration': {
      label: 'ROT registrering',
      type: 'program' as const,
      title: 'ROT registreringsavgift',
      amount: '1',
      message: 'Test av ROT registrering',
    },
    'rot-balance': {
      label: 'ROT slutbetalning',
      type: 'program' as const,
      title: 'ROT slutbetalning',
      amount: '1',
      message: 'Test av ROT slutbetalning',
    },
    booking: {
      label: 'Bokning',
      type: 'booking' as const,
      title: 'Bokningstest',
      amount: '1',
      message: 'Test av bokning',
    },
  };

  React.useEffect(() => {
    loadTemplates();
  }, []);

  React.useEffect(() => {
    const loadMembershipPlans = async () => {
      try {
        const plans = await MembershipPlanService.getPublic();
        setMembershipPlans(plans);
      } catch (err) {
        console.error('Failed to load membership plans', err);
      }
    };

    void loadMembershipPlans();
  }, []);

  React.useEffect(() => {
    const loadConsultingPricing = async () => {
      try {
        const data = await ConsultingPricingService.getAdmin();
        setConsultingPricing(data);
        if (data) {
          setConsultingRuleDraft(prev => ({
            ...prev,
            serviceKey: 'consulting',
          }));
        }
      } catch (err) {
        console.error('Failed to load consulting pricing', err);
      }
    };

    void loadConsultingPricing();
  }, []);

  React.useEffect(() => {
    if (!paymentStatus) return;
    if (paymentStatus === 'PAID') toast.success('Testbetalningen är godkänd.');
    if (paymentStatus === 'FAILED') toast.error('Testbetalningen misslyckades.');
  }, [paymentStatus]);

  React.useEffect(() => {
    if (!stripeStatus) return;
    if (stripeStatus === 'PAID') toast.success('Stripe testbetalningen är godkänd.');
    if (stripeStatus === 'FAILED') toast.error('Stripe testbetalningen misslyckades.');
  }, [stripeStatus]);

  React.useEffect(() => {
    if (stripeType !== 'booking') return;

    const loadOrCreateSlots = async () => {
      const existing = (await BookingService.GetAvailableTimeSlots()) ?? [];

      if (existing.length > 0) {
        setAvailableBookingSlots(existing.filter(slot => slot.id > 0));
        setSelectedBookingSlotId(prev => prev || String(existing[0].id));
        return;
      }

      const tomorrow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      const date = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
      const testSlot = {
        id: 0,
        date,
        time: '10:00',
        available: true,
        newTimeSlot: true,
      };

      const created = await BookingService.CreateAvailableTimeSlots([testSlot as any]);
      if (!created) return;

      const refreshed = (await BookingService.GetAvailableTimeSlots()) ?? [];
      setAvailableBookingSlots(refreshed.filter(slot => slot.id > 0));
      setSelectedBookingSlotId(refreshed[0] ? String(refreshed[0].id) : '');
    };

    void loadOrCreateSlots();
  }, [stripeType]);

  const loadTemplates = async () => {
    setLoading(true);
    const all = await EmailTemplateService.getAll();
    setTemplates(all.filter(t => t.type.startsWith('feelgood-')));
    setPublicTemplates(all.filter(t => t.type.startsWith('page-')));
    setLoading(false);
  };

  const handleSaveTemplate = async (dto: { name: string; subject: string; htmlContent: string; type: string; isActive: boolean }) => {
    const result = editingTemplate
      ? await EmailTemplateService.update(editingTemplate.id, dto)
      : await EmailTemplateService.create(dto);

    if (!result) {
      toast.error('Kunde inte spara texten.');
      return;
    }

    toast.success('Text sparad.');
    setEditingTemplate(null);
    setShowTemplateModal(false);
    await loadTemplates();
  };

  const handleSaveConsultingConfig = async () => {
    if (!consultingPricing) return;

    setConsultingSaving(true);
    const updated = await ConsultingPricingService.updateConfig({
      baseSessionPrice: consultingPricing.config.baseSessionPrice,
      currency: consultingPricing.config.currency,
      basePriceText: consultingPricing.config.basePriceText,
      pageIntroText: consultingPricing.config.pageIntroText ?? null,
      isActive: consultingPricing.config.isActive,
    });
    setConsultingSaving(false);

    if (!updated) {
      toast.error('Kunde inte spara konsultpris.');
      return;
    }

    toast.success('Konsultpris sparat.');
    const refreshed = await ConsultingPricingService.getAdmin();
    setConsultingPricing(refreshed);
  };

  const saveConsultingRule = async () => {
    const validationError = validateConsultingRuleDraft(consultingRuleDraft);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setConsultingSaving(true);
    const payload = normalizeConsultingRuleDraft(consultingRuleDraft);

    const result = editingConsultingRuleId
      ? await ConsultingPricingService.updateRule(editingConsultingRuleId, payload)
      : await ConsultingPricingService.createRule(payload);

    setConsultingSaving(false);

    if (!result) {
      toast.error('Kunde inte spara regeln.');
      return;
    }

    toast.success('Regel sparad.');
    setConsultingRuleDraft(emptyConsultingRule);
    setEditingConsultingRuleId(null);
    const refreshed = await ConsultingPricingService.getAdmin();
    setConsultingPricing(refreshed);
  };

  const editConsultingRule = (rule: ConsultingPricingRuleDto) => {
    setEditingConsultingRuleId(rule.id);
    setConsultingRuleDraft({
      serviceKey: rule.serviceKey,
      isActive: rule.isActive,
      priority: rule.priority,
      minSessions: rule.minSessions,
      ruleType: rule.ruleType,
      percentOff: rule.percentOff ?? 0,
      fixedTotalPrice: rule.fixedTotalPrice ?? null,
      freeSessions: rule.freeSessions ?? null,
      displayText: rule.displayText,
      snapshotLabel: rule.snapshotLabel ?? '',
      startAtUtc: rule.startAtUtc ?? null,
      endAtUtc: rule.endAtUtc ?? null,
    });
  };

  const deleteConsultingRule = async (id: number) => {
    if (!window.confirm('Ta bort denna prisregel?')) return;
    const ok = await ConsultingPricingService.deleteRule(id);
    if (ok) {
      toast.success('Regel borttagen.');
      const refreshed = await ConsultingPricingService.getAdmin();
      setConsultingPricing(refreshed);
    } else {
      toast.error('Kunde inte ta bort regeln.');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Ta bort denna text?')) return;

    const ok = await EmailTemplateService.delete(id);
    if (ok) {
      toast.success('Text borttagen.');
      await loadTemplates();
    } else {
      toast.error('Kunde inte ta bort texten.');
    }
  };

  const startTestPayment = async () => {
    const normalizedAmount = Number(amount || '1');
    if (!payerAlias.trim()) {
      toast.error('Fyll i Swish-nummer.');
      return;
    }

    const normalizedPayerAlias = normalizeSwishPayerAlias(payerAlias);
    if (!normalizedPayerAlias.value) {
      toast.error(normalizedPayerAlias.error ?? 'Ogiltigt Swish-nummer.');
      return;
    }

    try {
      const ref = await PaymentService.SendAdminTestSwishPaymentRequest({
        payerAlias: normalizedPayerAlias.value,
        amount: Number.isFinite(normalizedAmount) && normalizedAmount > 0 ? normalizedAmount : 1,
        message,
      });

      if (!ref) {
        toast.error('Kunde inte starta testbetalningen.');
        return;
      }

      setPaymentId(ref);
      toast.info('Testbetalning startad. Öppna Swish.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kunde inte starta testbetalningen.';
      toast.error(message);
    }
  };

  const startStripeTestPayment = async () => {
    const parsedAmount = Number(stripeAmount || '1');
    const normalizedAmount = Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : 1;
    const selectedSlot = availableBookingSlots.find(slot => String(slot.id) === selectedBookingSlotId);
    const formattedBookingSlot = selectedSlot
      ? `${selectedSlot.date.getDateOnly()} ${selectedSlot.startTime.getTime().slice(0, 5)}`
      : undefined;

    if (stripeType === 'membership') {
      const planKey = stripePreset === 'grow' ? 'premium' : 'basic';
      const plan = membershipPlans.find(x => x.key === planKey);
      const priceId = plan?.stripePriceId;

      if (!priceId) {
        toast.error(`Saknar Stripe PriceId för ${planKey}. Synka medlemsplanen först.`);
        return;
      }

      const subscription = await PaymentService.CreateSubscription({
        email: stripeContact.email,
        priceId,
        applicationUserId: undefined,
        planKey,
      });

      setStripePayment(null);
      setStripeSubscription({
        subscriptionId: subscription.subscriptionId,
        clientSecret: subscription.clientSecret,
        planKey,
      });

      toast.info(`Stripe subscription skapad för ${planKey}. Slutför betalningen nedan.`);
      return;
    }

    const request: CheckoutOrderRequest = {
      paymentMethod: 'stripe',
      currency: 'SEK',
      contact: stripeContact,
      items: [
        {
          itemType: stripeType,
          referenceId: `admin-test-${stripeType}`,
          title: stripeTitle || 'Testprodukt',
          courseId: stripeCourseId ? Number(stripeCourseId) : undefined,
          unitPrice: normalizedAmount,
          quantity: 1,
          bookingSlots: stripeType === 'booking' && formattedBookingSlot ? [formattedBookingSlot] : undefined,
          metadataJson: JSON.stringify({
            adminTest: true,
            productType: stripeType,
            planKey: stripePreset === 'grow' ? 'premium' : undefined,
            programKey: stripePreset === 'rot-registration' || stripePreset === 'rot-balance' ? 'rot' : undefined,
            bookingMode: stripeType === 'booking' ? 'paid' : undefined,
          })
        }
      ]
    };

    const order = await PaymentService.CreateCheckoutSession(request);
    if (!order?.paymentReference) {
      toast.error('Kunde inte skapa Stripe-order.');
      return;
    }

    setStripeSubscription(null);
    setStripePayment({ paymentReference: order.paymentReference, amount: order.totalAmount });
    toast.info('Stripe test skapad. Slutför betalningen nedan.');
  };

  const applyStripePreset = (presetKey: keyof typeof stripePresets) => {
    const preset = stripePresets[presetKey];
    setStripePreset(presetKey);
    setStripeType(preset.type);
    setStripeTitle(preset.title);
    setStripeAmount(preset.amount);
    setMessage(preset.message);
  };

  return (
    <div className="admin-page-shell">
      <header className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Portalverktyg</h1>
          <p className="admin-page-subtitle">Feelgoodtexter för dashboarden och live Swish-test i produktion.</p>
        </div>
      </header>

      <div className="admin-tools-shell">
        <section className="admin-surface-card admin-campain-main-card admin-tools-hero">
          <div className="admin-tools-hero-copy">
            <Badge variant="secondary" className="admin-tools-hero-badge">Verktyg</Badge>
            <h2 className="admin-tools-hero-title">Samlad kontrollpanel</h2>
            <p className="admin-tools-hero-text">Redigera textblock, prisregler och testflöden i en vy som prioriterar skanning, tydliga statusar och mobil respons.</p>
          </div>

          <div className="admin-tools-overview-grid">
            <div className="admin-tools-overview-card">
              <span>Feelgood texter</span>
              <strong>{loading ? '...' : feelgoodCount}</strong>
              <p>Aktiva och utkastade texter för medlem och kursägare.</p>
            </div>
            <div className="admin-tools-overview-card">
              <span>Publik copy</span>
              <strong>{loading ? '...' : publicCopyCount}</strong>
              <p>Textblock som visas publikt på sidor och landningsytor.</p>
            </div>
            <div className="admin-tools-overview-card">
              <span>Prisregler</span>
              <strong>{consultingPricing ? consultingRuleCount : '...'}</strong>
              <p>Baspris och aktiva paketregler för 1:1-flödet.</p>
            </div>
            <div className="admin-tools-overview-card">
              <span>Teststatus</span>
              <Badge variant="outline" className="admin-tools-overview-state">Swish: {swishState}</Badge>
              <Badge variant="outline" className="admin-tools-overview-state">Stripe: {stripeState}</Badge>
            </div>
          </div>
        </section>

      <Tabs defaultValue="feelgood" className="admin-tools-tabs">
        <TabsList className="admin-campain-main-tabs admin-tools-tabs-list">
          <TabsTrigger value="feelgood">Feelgood texter</TabsTrigger>
          <TabsTrigger value="publiccopy">Publik copy</TabsTrigger>
          <TabsTrigger value="consulting">1:1 priser</TabsTrigger>
          <TabsTrigger value="testpayment">Swish testbetalning</TabsTrigger>
          <TabsTrigger value="logs">Loggar</TabsTrigger>
        </TabsList>

        <TabsContent value="feelgood">
          <section className="admin-surface-card admin-campain-main-card admin-tools-panel admin-tools-panel--stack">
            <div className="admin-tools-section-title">
              <div>
                <h2>Feelgood texter</h2>
                <p>Visas på medlemmars och kursägares dashboard beroende på målgrupp.</p>
              </div>
              <Button
                className="submit-button"
                text="Skapa ny text"
                onClick={() => {
                  setTemplateMode('feelgood');
                  setEditingTemplate(null);
                  setShowTemplateModal(true);
                }}
              />
            </div>

            {loading ? (
              <p>Laddar texter...</p>
            ) : templates.length === 0 ? (
              <p>Inga feelgoodtexter ännu.</p>
            ) : (
              <div className="admin-tools-card-grid">
                {templates.map(template => (
                  <Card key={template.id} className="admin-surface-card" disableHover>
                    <CardHeader>
                      <CardTitle>{template.name}</CardTitle>
                      <CardDescription>{template.subject}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="admin-tools-card-meta-row">
                        <div className="admin-tools-card-meta-stack">
                          <Badge variant={template.isActive ? 'default' : 'secondary'}>{template.isActive ? 'Aktiv' : 'Utkast'}</Badge>
                          <span className="admin-tools-card-meta-label">Målgrupp</span>
                          <strong>{feelgoodLabel(template.type)}</strong>
                        </div>
                        <div className="admin-tools-card-actions">
                          <Button
                            className="basic-button"
                            text="Redigera"
                            onClick={() => {
                              setTemplateMode('feelgood');
                              setEditingTemplate(template);
                              setShowTemplateModal(true);
                            }}
                          />
                          <Button
                            className="basic-button"
                            text="Ta bort"
                            onClick={() => handleDeleteTemplate(template.id)}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {showTemplateModal && (
              <EmailTemplateModal
                template={editingTemplate}
                variant={templateMode}
                onClose={() => setShowTemplateModal(false)}
                onSubmit={handleSaveTemplate}
              />
            )}
          </section>
        </TabsContent>

        <TabsContent value="publiccopy">
          <section className="admin-surface-card admin-campain-main-card admin-tools-panel admin-tools-panel--stack">
            <div className="admin-tools-section-title">
              <div>
                <h2>Publik copy</h2>
                <p>Editerbara textblock för startsida, om-sida och medlemskap.</p>
              </div>
              <Button
                className="submit-button"
                text="Skapa nytt block"
                onClick={() => {
                  setTemplateMode('public');
                  setEditingTemplate(null);
                  setShowTemplateModal(true);
                }}
              />
            </div>

            {loading ? (
              <p>Laddar texter...</p>
            ) : publicTemplates.length === 0 ? (
              <p>Inga publika block ännu.</p>
            ) : (
              <div className="admin-tools-card-grid">
                {publicTemplates.map(template => (
                  <Card key={template.id} className="admin-surface-card" disableHover>
                    <CardHeader>
                      <CardTitle>{template.name}</CardTitle>
                      <CardDescription>{template.type}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="admin-tools-card-meta-row">
                        <div className="admin-tools-card-meta-stack">
                          <Badge variant={template.isActive ? 'default' : 'secondary'}>{template.isActive ? 'Aktiv' : 'Utkast'}</Badge>
                          <span className="admin-tools-card-meta-label">Rubrik</span>
                          <strong>{template.subject}</strong>
                        </div>
                        <div className="admin-tools-card-actions">
                          <Button
                            className="basic-button"
                            text="Redigera"
                            onClick={() => {
                              setTemplateMode('public');
                              setEditingTemplate(template);
                              setShowTemplateModal(true);
                            }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {showTemplateModal && templateMode === 'public' && (
              <EmailTemplateModal
                template={editingTemplate}
                variant="public"
                onClose={() => setShowTemplateModal(false)}
                onSubmit={handleSaveTemplate}
              />
            )}
          </section>
        </TabsContent>

        <TabsContent value="consulting">
          <section className="admin-surface-card admin-campain-main-card admin-tools-panel admin-tools-panel--stack">
            <div className="admin-tools-section-title">
              <div>
                <h2>1:1 priser och paket</h2>
                <p>Styr baspris, aktiv rabatttext och paketerade samtalsregler.</p>
              </div>
              <Button
                className="submit-button"
                text="Ny regel"
                onClick={() => {
                  setEditingConsultingRuleId(null);
                  setConsultingRuleDraft(emptyConsultingRule);
                }}
              />
            </div>

            {consultingPricing && (
              <div className="admin-tools-split-layout">
                <Card className="admin-surface-card" disableHover>
                  <CardHeader>
                    <CardTitle>Baspris</CardTitle>
                    <CardDescription>Visas på konsultsidan och används som utgångspris.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="admin-tools-form">
                      <label>
                        <span>Pris per samtal</span>
                        <input
                          className="admin-tools-input"
                          type="number"
                          value={consultingPricing.config.baseSessionPrice}
                          onChange={e => setConsultingPricing({ ...consultingPricing, config: { ...consultingPricing.config, baseSessionPrice: Number(e.target.value) } })}
                        />
                      </label>
                      <label>
                        <span>Pristext</span>
                        <input
                          className="admin-tools-input"
                          value={consultingPricing.config.basePriceText}
                          onChange={e => setConsultingPricing({ ...consultingPricing, config: { ...consultingPricing.config, basePriceText: e.target.value } })}
                        />
                      </label>
                      <label>
                        <span>Introtext på sidan</span>
                        <textarea
                          className="admin-tools-input"
                          value={consultingPricing.config.pageIntroText ?? ''}
                          onChange={e => setConsultingPricing({ ...consultingPricing, config: { ...consultingPricing.config, pageIntroText: e.target.value } })}
                        />
                      </label>
                      <Button className="submit-button" text={consultingSaving ? 'Sparar...' : 'Spara baspris'} onClick={handleSaveConsultingConfig} />
                    </div>
                  </CardContent>
                </Card>

                <Card className="admin-surface-card" disableHover>
                  <CardHeader>
                    <CardTitle>Aktiva regler</CardTitle>
                    <CardDescription>Reglerna visas på konsultsidan och används i bokningskassan.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="admin-tools-card-grid">
                      {(consultingPricing.rules ?? []).map(rule => (
                        <div key={rule.id} className="admin-tools-rule-card">
                          <div className="admin-tools-card-meta-row">
                            <div className="admin-tools-card-meta-stack">
                              <Badge variant={rule.isActive ? 'default' : 'secondary'}>{rule.isActive ? 'Aktiv' : 'Inaktiv'}</Badge>
                              <strong>{rule.displayText}</strong>
                              <p>Typ: {ConsultingPricingRuleType[rule.ruleType]} · Min: {rule.minSessions} · Prioritet: {rule.priority}</p>
                            </div>
                            <div className="admin-tools-card-actions">
                              <Button className="basic-button" text="Redigera" onClick={() => editConsultingRule(rule)} />
                              <Button className="basic-button" text="Ta bort" onClick={() => deleteConsultingRule(rule.id)} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="admin-surface-card" disableHover>
              <CardHeader>
                <CardTitle>{editingConsultingRuleId ? 'Redigera regel' : 'Skapa regel'}</CardTitle>
                <CardDescription>Stöder procent, fast total och köp X få Y gratis.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="admin-tools-form">
                  <label>
                    <span>Aktiv</span>
                    <select className="admin-tools-input" value={String(consultingRuleDraft.isActive)} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, isActive: e.target.value === 'true' })}>
                      <option value="true">Ja</option>
                      <option value="false">Nej</option>
                    </select>
                  </label>
                  <label>
                    <span>Prioritet</span>
                    <input className="admin-tools-input" type="number" value={consultingRuleDraft.priority} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, priority: Number(e.target.value) })} />
                  </label>
                  <label>
                    <span>Min antal samtal</span>
                    <input className="admin-tools-input" type="number" value={consultingRuleDraft.minSessions} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, minSessions: Number(e.target.value) })} />
                  </label>
                  <label>
                    <span>Regeltyp</span>
                    <select
                      className="admin-tools-input"
                      value={consultingRuleDraft.ruleType}
                      onChange={e => {
                        const ruleType = Number(e.target.value) as ConsultingPricingRuleType;
                        setConsultingRuleDraft(prev => ({
                          ...prev,
                          ruleType,
                          percentOff: ruleType === ConsultingPricingRuleType.Percentage ? prev.percentOff ?? 0 : null,
                          fixedTotalPrice: ruleType === ConsultingPricingRuleType.FixedTotal ? prev.fixedTotalPrice ?? null : null,
                          freeSessions: ruleType === ConsultingPricingRuleType.BuyXGetY ? prev.freeSessions ?? 1 : null,
                        }));
                      }}
                    >
                      <option value={ConsultingPricingRuleType.Percentage}>Procent</option>
                      <option value={ConsultingPricingRuleType.FixedTotal}>Fast total</option>
                      <option value={ConsultingPricingRuleType.BuyXGetY}>Köp X få Y gratis</option>
                    </select>
                  </label>
                  {consultingRuleDraft.ruleType === ConsultingPricingRuleType.Percentage && (
                    <label>
                      <span>Procent av</span>
                      <input className="admin-tools-input" type="number" min="1" max="100" value={consultingRuleDraft.percentOff ?? ''} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, percentOff: e.target.value === '' ? null : Number(e.target.value) })} />
                    </label>
                  )}
                  {consultingRuleDraft.ruleType === ConsultingPricingRuleType.FixedTotal && (
                    <label>
                      <span>Fast totalpris</span>
                      <input className="admin-tools-input" type="number" min="0" value={consultingRuleDraft.fixedTotalPrice ?? ''} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, fixedTotalPrice: e.target.value === '' ? null : Number(e.target.value) })} />
                    </label>
                  )}
                  {consultingRuleDraft.ruleType === ConsultingPricingRuleType.BuyXGetY && (
                    <label>
                      <span>Gratis samtal</span>
                      <input className="admin-tools-input" type="number" min="1" value={consultingRuleDraft.freeSessions ?? ''} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, freeSessions: e.target.value === '' ? null : Number(e.target.value) })} />
                    </label>
                  )}
                  <label>
                    <span>Synlig text</span>
                    <input className="admin-tools-input" value={consultingRuleDraft.displayText} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, displayText: e.target.value })} />
                  </label>
                  <label>
                    <span>Snapshot-label</span>
                    <input className="admin-tools-input" value={consultingRuleDraft.snapshotLabel ?? ''} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, snapshotLabel: e.target.value })} />
                  </label>
                  <label>
                    <span>Startdatum</span>
                    <input className="admin-tools-input" type="datetime-local" value={toDateTimeLocalValue(consultingRuleDraft.startAtUtc)} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, startAtUtc: fromDateTimeLocalValue(e.target.value) })} />
                  </label>
                  <label>
                    <span>Slutdatum</span>
                    <input className="admin-tools-input" type="datetime-local" value={toDateTimeLocalValue(consultingRuleDraft.endAtUtc)} onChange={e => setConsultingRuleDraft({ ...consultingRuleDraft, endAtUtc: fromDateTimeLocalValue(e.target.value) })} />
                  </label>
                  <Button className="submit-button" text={consultingSaving ? 'Sparar...' : 'Spara regel'} onClick={saveConsultingRule} />
                </div>
              </CardContent>
            </Card>
          </section>
        </TabsContent>

        <TabsContent value="testpayment">
          <section className="admin-surface-card admin-campain-main-card admin-tools-panel admin-tools-panel--stack">
            <div className="admin-tools-split-layout">
              <Card className="admin-surface-card admin-tools-payment-card" disableHover>
                <CardHeader>
                  <CardTitle>Swish testbetalning</CardTitle>
                  <CardDescription>Startar ett live Swish-flöde utan att skapa medlemskap eller kursåtkomst.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="admin-tools-form">
                    <label>
                      <span>Swish-nummer</span>
                      <input className="admin-tools-input" value={payerAlias} onChange={e => setPayerAlias(e.target.value)} placeholder="0701234567" />
                      <p style={{ marginTop: 6, fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                        Ange 07XXXXXXXX eller 467XXXXXXXX. Backend normaliserar formatet innan Swish-anropet skickas.
                      </p>
                    </label>
                    <label>
                      <span>Belopp</span>
                      <input className="admin-tools-input" value={amount} onChange={e => setAmount(e.target.value)} />
                    </label>
                    <label>
                      <span>Meddelande</span>
                      <input className="admin-tools-input" value={message} onChange={e => setMessage(e.target.value)} />
                    </label>
                    <Button className="submit-button" text="Starta testbetalning" onClick={startTestPayment} />
                    {paymentId && (
                      <div className="admin-stat-card">
                        <span>Payment reference</span>
                        <strong>{paymentId}</strong>
                        <p>Status: {swishState}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="admin-surface-card admin-tools-payment-card" disableHover>
                <CardHeader>
                  <CardTitle>Stripe testbetalning</CardTitle>
                  <CardDescription>Skapar en riktig checkout-session för en testprodukt och visar Stripe-flödet direkt.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="admin-tools-form">
                    <div className="admin-tools-preset-row">
                      {Object.entries(stripePresets).map(([key, preset]) => (
                        <button
                          key={key}
                          type="button"
                          className={`admin-tools-preset-chip ${stripePreset === key ? 'active' : ''}`}
                          onClick={() => applyStripePreset(key as keyof typeof stripePresets)}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <label>
                      <span>Produkttyp</span>
                      <select className="admin-tools-input" value={stripeType} onChange={e => setStripeType(e.target.value as any)}>
                        <option value="membership">Medlemskap</option>
                        <option value="course">Kurs</option>
                        <option value="program">Program</option>
                        <option value="booking">Bokning</option>
                      </select>
                    </label>
                    <label>
                      <span>Titel</span>
                      <input className="admin-tools-input" value={stripeTitle} onChange={e => setStripeTitle(e.target.value)} placeholder="Ex: ROT testprodukt" />
                    </label>
                    <label>
                      <span>Belopp</span>
                      <input className="admin-tools-input" value={stripeAmount} onChange={e => setStripeAmount(e.target.value)} />
                    </label>
                    <label>
                      <span>Kurs-ID (valfritt)</span>
                      <input className="admin-tools-input" value={stripeCourseId} onChange={e => setStripeCourseId(e.target.value)} placeholder="123" />
                    </label>
                    {stripeType === 'booking' && (
                      <label>
                        <span>Bokningsslot</span>
                        <select
                          className="admin-tools-input"
                          value={selectedBookingSlotId}
                          onChange={e => setSelectedBookingSlotId(e.target.value)}
                        >
                          <option value="">Välj ledig tid</option>
                          {availableBookingSlots.map(slot => (
                            <option key={slot.id} value={slot.id}>
                              {slot.date.getDateOnly()} {slot.startTime.getTime().slice(0, 5)}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}

                    <div className="dashboard-feelgood-grid" style={{ marginBottom: 0 }}>
                      <label>
                        <span>Förnamn</span>
                        <input className="admin-tools-input" value={stripeContact.firstName} onChange={e => setStripeContact(prev => ({ ...prev, firstName: e.target.value }))} />
                      </label>
                      <label>
                        <span>Efternamn</span>
                        <input className="admin-tools-input" value={stripeContact.lastName} onChange={e => setStripeContact(prev => ({ ...prev, lastName: e.target.value }))} />
                      </label>
                      <label>
                        <span>Email</span>
                        <input className="admin-tools-input" value={stripeContact.email} onChange={e => setStripeContact(prev => ({ ...prev, email: e.target.value }))} />
                      </label>
                      <label>
                        <span>Telefon</span>
                        <input className="admin-tools-input" value={stripeContact.phone} onChange={e => setStripeContact(prev => ({ ...prev, phone: e.target.value }))} />
                      </label>
                    </div>

                    <Button className="submit-button" text="Skapa Stripe test" onClick={startStripeTestPayment} />
                  </div>

                  {stripeSubscription && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <div className="admin-stat-card" style={{ marginBottom: '1rem' }}>
                        <span>Stripe subscription</span>
                        <strong>{stripeSubscription.subscriptionId}</strong>
                        <p>Plan: {stripeSubscription.planKey}</p>
                      </div>

                      <div className="admin-tools-preview">
                        <StripeCheckoutWrapper
                          clientSecret={stripeSubscription.clientSecret}
                          onSuccess={() => toast.success('Stripe subscription klar.')}
                        />
                      </div>
                    </div>
                  )}

                  {stripePayment && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <div className="admin-stat-card" style={{ marginBottom: '1rem' }}>
                        <span>Stripe payment reference</span>
                        <strong>{stripePayment.paymentReference}</strong>
                        <p>Belopp: {stripePayment.amount.toFixed(2)} SEK</p>
                      </div>

                      <div className="admin-tools-preview">
                        <StripeCheckoutWrapper
                          amount={stripePayment.amount}
                          paymentReference={stripePayment.paymentReference}
                          onSuccess={() => toast.success('Stripe testbetalning klar.')}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="admin-surface-card admin-tools-payment-card" disableHover>
                <CardHeader>
                  <CardTitle>Live status</CardTitle>
                  <CardDescription>Följ Swish- och Stripe-testbetalningar i realtid.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="admin-tools-status-grid">
                    <div className="admin-tools-status-item">
                      <span>Swish</span>
                      <strong>{paymentId ?? 'Ingen pågående testbetalning'}</strong>
                      <Badge variant="outline">{swishState}</Badge>
                      <p>{paymentId ? 'Följ status direkt efter att betalningen startats.' : 'Starta en testbetalning för att se status.'}</p>
                    </div>
                    <div className="admin-tools-status-item">
                      <span>Stripe</span>
                      <strong>{stripePayment?.paymentReference ?? 'Ingen pågående checkout'}</strong>
                      <Badge variant="outline">{stripeState}</Badge>
                      <p>{stripePayment ? 'Checkout-sessionen är aktiv och kan följas här.' : 'Skapa en Stripe-test för att se status.'}</p>
                    </div>
                    <div className="admin-tools-status-item">
                      <span>Subscription</span>
                      <strong>{stripeSubscription?.subscriptionId ?? 'Ingen pågående subscription'}</strong>
                      <Badge variant="outline">{stripeSubscription ? `Plan: ${stripeSubscription.planKey}` : 'Ingen aktiv subscription'}</Badge>
                      <p>{stripeSubscription ? 'Medlemskapstestet är igång och går att verifiera i checkout-komponenten.' : 'Skapa ett medlemskapstest för att se subscription.'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="logs">
          <AdminLogsPanel />
        </TabsContent>
      </Tabs>
      </div>

      <ToastContainer position="bottom-right" />
    </div>
  );
};
